import { useEffect, useMemo, useState } from 'react'
import { PanelLeft } from 'lucide-react'
import {
  REQUIREMENT_DOC_SECTION_LABELS,
  REQUIREMENT_DOC_SECTIONS,
  DEFAULT_REQUIREMENT_SECTION,
  type RequirementNode,
  type RequirementSection,
} from '@ispot/shared'
import { useRequirementsViewStore, type RequirementsGroupBy } from '@/stores/requirementsViewStore'
import { RequirementDetailPanel } from '../RequirementDetailPanel'
import { ViewToolbar } from '../filters/ViewToolbar'
import { OutlineRow } from '../OutlineRow'
import { OutlineTOC } from '../OutlineTOC'
import { useOutlineHandlers } from '../useOutlineHandlers'
import type { ViewContainerProps } from './sharedProps'

interface FlatRow {
  id: string
  depth: 0 | 1
  section: RequirementSection
}

function epicSection(epic: RequirementNode): RequirementSection {
  const s = epic.metadata?.section as RequirementSection | undefined
  return s && REQUIREMENT_DOC_SECTIONS.includes(s) ? s : DEFAULT_REQUIREMENT_SECTION
}

function personaKey(node: RequirementNode): string {
  const primary = node.personas?.find(p => p.isPrimary) ?? node.personas?.[0]
  if (primary?.name) return primary.name
  const legacy = node.metadata?.linkedPersona as { name?: string } | undefined
  return legacy?.name ?? 'No persona'
}

function groupKey(node: RequirementNode, groupBy: RequirementsGroupBy, themes: ViewContainerProps['themes']): string {
  if (groupBy === 'persona') return personaKey(node)
  if (groupBy === 'status') return node.status || 'No status'
  if (groupBy === 'priority') return node.priority ?? 'No priority'
  if (groupBy === 'theme') {
    const t = node.primaryThemeId ? themes.find(th => th.id === node.primaryThemeId) : null
    return t?.name ?? 'No theme'
  }
  return 'Other'
}

// ─── Persona avatar shown in group headers ──────────────────────────────────

interface AvatarProps { name: string; photo?: string; size?: number }
function PersonaAvatar({ name, photo, size = 26 }: AvatarProps) {
  const initials = name.split(/\s+/).filter(Boolean).map(w => w[0]?.toUpperCase() ?? '').slice(0, 2).join('')
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-market-100)', color: 'var(--color-market-700)', fontSize: size * 0.42, fontWeight: 600, marginRight: 6 }}>
      {photo
        ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
        : initials
      }
    </span>
  )
}

export function OutlineView({
  accountId, projectId,
  requirements, themes,
  tree,
  personaIndex,
  detailNode, detailId, setDetailId,
  updateRequirement, deleteRequirement,
  createBlock, reorderSiblings,
  resolvePersona,
  errorBanner,
}: ViewContainerProps) {
  const groupBy = useRequirementsViewStore(s => s.groupBy)

  const [tocOpen, setTocOpen] = useState(false)
  const [tocPinned, setTocPinned] = useState(true)

  // Auto-close TOC when detail panel opens, unless pinned
  useEffect(() => {
    if (detailNode && tocOpen && !tocPinned) setTocOpen(false)
  }, [detailNode])

  // Hierarchy mode: group epics by document section
  const hierarchyGrouped = useMemo(() => {
    const byId: Record<RequirementSection, RequirementNode[]> = {
      overview: [],
      functional: [],
      'non-functional': [],
    }
    for (const node of tree) {
      if (node.type === 'epic' || node.type === 'nfr-category') {
        byId[epicSection(node)].push(node)
      }
    }
    return byId
  }, [tree])

  // Non-hierarchy mode: flatten and group by chosen axis (persona mode handled by personaGroups)
  const flatGrouped = useMemo((): Map<string, RequirementNode[]> | null => {
    if (groupBy === 'hierarchy' || groupBy === 'persona') return null
    const groups = new Map<string, RequirementNode[]>()
    for (const epic of tree) {
      const epicK = groupKey(epic, groupBy, themes)
      if (!groups.has(epicK)) groups.set(epicK, [])
      groups.get(epicK)!.push(epic)
      for (const story of epic.children) {
        const storyK = groupKey(story, groupBy, themes)
        if (!groups.has(storyK)) groups.set(storyK, [])
        groups.get(storyK)!.push(story)
      }
    }
    return groups
  }, [groupBy, tree, themes])

  // Persona groups: stories are grouped by their own linked personas; epics are
  // repeated as context in every persona group served by their descendant stories.
  type PersonaGroupRow = { node: RequirementNode; depth: 0 | 1 }
  type PersonaGroup = { key: string; name: string; role?: string; photo?: string; unassigned: boolean; rows: PersonaGroupRow[] }
  const personaGroups = useMemo((): PersonaGroup[] | null => {
    if (groupBy !== 'persona') return null
    const byKey = new Map<string, PersonaGroup>()
    const seen = new Map<string, Set<string>>()
    const add = (key: string, meta: Omit<PersonaGroup, 'rows' | 'key'>, node: RequirementNode, depth: 0 | 1) => {
      if (!byKey.has(key)) byKey.set(key, { key, ...meta, rows: [] })
      const seenIds = seen.get(key) ?? new Set<string>()
      if (seenIds.has(node.id)) return
      seenIds.add(node.id)
      seen.set(key, seenIds)
      byKey.get(key)!.rows.push({ node, depth })
    }

    const targetsFor = (node: RequirementNode) => {
      if (node.personas?.length) {
        return node.personas.map((persona) => {
          const idx = personaIndex.get(persona.personaSessionId)
          return {
            key: `p:${persona.personaSessionId}`,
            meta: {
              name: persona.name || idx?.name || `Persona ${persona.personaSessionId.slice(0, 6)}`,
              role: idx?.role,
              photo: persona.photo || idx?.photo,
              unassigned: false,
            },
          }
        })
      }

      const legacyName = (node.metadata?.linkedPersona as { name?: string } | undefined)?.name
      if (legacyName) {
        const lookup = resolvePersona(legacyName)
        return [{
          key: `legacy:${legacyName}`,
          meta: { name: legacyName, role: lookup?.role, photo: lookup?.photo, unassigned: false },
        }]
      }

      return []
    }

    const unassigned = { name: 'No persona', unassigned: true }

    for (const epic of tree) {
      const epicTargets = targetsFor(epic)
      const children = epic.children ?? []
      for (const target of epicTargets) add(target.key, target.meta, epic, 0)

      if (children.length === 0) {
        const targets = epicTargets.length ? epicTargets : [{ key: 'no-persona', meta: unassigned }]
        for (const target of targets) add(target.key, target.meta, epic, 0)
        continue
      }

      for (const story of children) {
        const storyTargets = targetsFor(story)
        const targets = storyTargets.length
          ? storyTargets
          : epicTargets.length
            ? epicTargets
            : [{ key: 'no-persona', meta: unassigned }]

        for (const target of targets) {
          add(target.key, target.meta, epic, 0)
          add(target.key, target.meta, story, 1)
        }
      }
    }
    return Array.from(byKey.values()).sort((a, b) => {
      if (a.unassigned) return 1
      if (b.unassigned) return -1
      return a.name.localeCompare(b.name)
    })
  }, [groupBy, tree, personaIndex, resolvePersona])

  const flatOrder: FlatRow[] = useMemo(() => {
    if (personaGroups) {
      return personaGroups.flatMap(g => (
        g.rows.map(row => ({ id: row.node.id, depth: row.depth, section: DEFAULT_REQUIREMENT_SECTION }))
      ))
    }
    if (flatGrouped) {
      const list: FlatRow[] = []
      for (const nodes of flatGrouped.values()) {
        for (const node of nodes) {
          list.push({ id: node.id, depth: 0, section: DEFAULT_REQUIREMENT_SECTION })
        }
      }
      return list
    }
    const list: FlatRow[] = []
    for (const sectionId of REQUIREMENT_DOC_SECTIONS) {
      for (const epic of hierarchyGrouped[sectionId] ?? []) {
        list.push({ id: epic.id, depth: 0, section: sectionId })
        for (const story of epic.children) {
          list.push({ id: story.id, depth: 1, section: sectionId })
        }
      }
    }
    return list
  }, [personaGroups, flatGrouped, hierarchyGrouped])

  const h = useOutlineHandlers({
    requirements, grouped: hierarchyGrouped, flatOrder,
    detailId, setDetailId,
    updateRequirement, deleteRequirement,
    createBlock, reorderSiblings,
  })

  const sectionsToShow = (() => {
    const populated = REQUIREMENT_DOC_SECTIONS.filter(s => (hierarchyGrouped[s] ?? []).length > 0)
    return populated.length === 0 ? REQUIREMENT_DOC_SECTIONS : populated
  })()

  const rowProps = (node: RequirementNode, depth: 0 | 1) => ({
    node,
    depth,
    isActive: detailId === node.id,
    draggingId: h.draggingId,
    dropEdge: h.dropTarget?.id === node.id ? h.dropTarget.edge : null,
    onOpenDetail: setDetailId,
    onCommit: h.handleCommit,
    onEnterAfter: h.handleEnterAfter,
    onIndent: h.handleIndent,
    onOutdent: h.handleOutdent,
    onDeleteIfEmpty: h.handleDeleteIfEmpty,
    onDelete: h.handleDeleteIfEmpty,
    onArrow: h.handleArrow,
    onAddBelow: h.handleAddBelow,
    onDragStart: h.handleDragStart,
    onDragOver: h.handleDragOver,
    onDragEnd: h.handleDragEnd,
    onDrop: h.handleDrop,
  })

  const tocToggleBtn = (
    <button
      type="button"
      onClick={() => setTocOpen(o => !o)}
      title={tocOpen ? 'Hide outline' : 'Show outline'}
      aria-pressed={tocOpen}
      style={{
        width: 28, height: 28,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'background 120ms, color 120ms',
        background: tocOpen ? 'var(--color-warm-200)' : 'transparent',
        color: tocOpen ? 'var(--color-fg)' : 'var(--color-fg-muted)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-warm-200)'; e.currentTarget.style.color = 'var(--color-fg)' }}
      onMouseLeave={e => { e.currentTarget.style.background = tocOpen ? 'var(--color-warm-200)' : 'transparent'; e.currentTarget.style.color = tocOpen ? 'var(--color-fg)' : 'var(--color-fg-muted)' }}
    >
      <PanelLeft size={15} />
    </button>
  )

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      <ViewToolbar requirements={requirements} themes={themes} leading={tocToggleBtn} />

      {errorBanner}

      <div className="flex flex-1 min-h-0">
        {tocOpen && (
          <OutlineTOC
            tree={tree}
            requirements={requirements}
            pinned={tocPinned}
            onTogglePin={() => setTocPinned(p => !p)}
            onClose={() => setTocOpen(false)}
          />
        )}
        <div
          className="flex-1 min-w-0 overflow-y-auto"
          style={{ background: 'var(--color-bg-card)' }}
        >
          <div className="mx-auto" style={{ maxWidth: 880, padding: '40px 24px 140px' }}>
            <h1
              className="m-0"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 28, fontWeight: 600, letterSpacing: '-0.015em',
                color: 'var(--color-fg)', marginBottom: 4,
              }}
            >
              Requirements
            </h1>
            <p style={{ color: 'var(--color-fg-muted)', fontSize: 13, marginBottom: 24 }}>
              A living outline. Click to edit, ↩ for new line, ⇥ to indent, click bullet to open detail.
            </p>

            {personaGroups ? (
              /* Persona grouping — enriched, photo avatars, unassigned last */
              personaGroups.map(group => (
                <section key={group.key} style={{ marginTop: 32 }}>
                  <header className="outline-section-header" style={group.unassigned ? { opacity: 0.55 } : undefined}>
                    {!group.unassigned && <PersonaAvatar name={group.name} photo={group.photo} />}
                    <span>{group.name}</span>
                    {group.role && <span className="meta" style={{ fontStyle: 'italic', marginRight: 8 }}>{group.role}</span>}
                    <span className="meta">{group.rows.length} {group.rows.length === 1 ? 'item' : 'items'}</span>
                  </header>
                  {group.rows.map(row => (
                    <OutlineRow key={row.node.id} {...rowProps(row.node, row.depth)} />
                  ))}
                </section>
              ))
            ) : flatGrouped ? (
              /* Other non-hierarchy grouping (status, priority, theme) */
              Array.from(flatGrouped.entries()).map(([label, nodes]) => (
                <section key={label} style={{ marginTop: 32 }}>
                  <header className="outline-section-header">
                    {label}
                    <span className="meta">
                      {nodes.length} {nodes.length === 1 ? 'item' : 'items'}
                    </span>
                  </header>
                  {nodes.map(node => (
                    <OutlineRow key={node.id} {...rowProps(node, 0)} />
                  ))}
                </section>
              ))
            ) : (
              /* Hierarchy grouping (default) */
              sectionsToShow.map(sectionId => {
                const epics = hierarchyGrouped[sectionId] ?? []
                return (
                  <section key={sectionId} style={{ marginTop: 32 }}>
                    <header className="outline-section-header">
                      {REQUIREMENT_DOC_SECTION_LABELS[sectionId]}
                      <span className="meta">
                        {epics.length} {epics.length === 1 ? 'epic' : 'epics'}
                      </span>
                    </header>

                    {epics.length === 0 ? (
                      <button
                        type="button"
                        className="outline-empty-add"
                        style={{
                          marginLeft: 32,
                          padding: '5px 10px',
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--color-fg-muted)',
                          fontSize: 13,
                          cursor: 'pointer',
                          opacity: 0.6,
                          borderRadius: 4,
                        }}
                        onClick={async () => {
                          const created = await createBlock({
                            type: 'epic', name: '', section: sectionId,
                          })
                          h.pendingFocusRef.current = { id: created.id, where: 'start' }
                        }}
                      >
                        + Add an epic to {REQUIREMENT_DOC_SECTION_LABELS[sectionId]}
                      </button>
                    ) : (
                      epics.map(epic => (
                        <div key={epic.id}>
                          <OutlineRow {...rowProps(epic, 0)} />
                          {epic.children.map(story => (
                            <OutlineRow key={story.id} {...rowProps(story, 1)} />
                          ))}
                        </div>
                      ))
                    )}
                  </section>
                )
              })
            )}
          </div>
        </div>

        {detailNode && (() => {
          const idx = flatOrder.findIndex(r => r.id === detailNode.id)
          const canPrev = idx > 0
          const canNext = idx >= 0 && idx < flatOrder.length - 1
          return (
            <RequirementDetailPanel
              accountId={accountId}
              projectId={projectId}
              node={detailNode}
              position={idx >= 0 ? { index: idx + 1, total: flatOrder.length } : undefined}
              onUpdate={updateRequirement}
              onDelete={deleteRequirement}
              onClose={() => setDetailId(null)}
              onPrev={canPrev ? () => setDetailId(flatOrder[idx - 1].id) : undefined}
              onNext={canNext ? () => setDetailId(flatOrder[idx + 1].id) : undefined}
              resolvePersona={resolvePersona}
            />
          )
        })()}
      </div>
    </div>
  )
}
